import { useCallback, useEffect, useState } from 'react';
import { numberToLocaleString, parseFromCurrencyString } from 'modules/utils';
import { Flex } from '../flex';
import { SufixType } from '../input/common/SufixType';
import { BodyText } from '../typography';
import {
  SliderContainer,
  SliderNotch,
  SliderNotchContainer,
  SliderNumberInput,
  SliderRangeInput,
  SliderTrack,
} from './styled';
import {SliderBody} from "./SliderBody";

export interface SliderProps {
  min: number;
  max: number;
  valueType?: SufixType;
  defaultValue?: number | string;
  notches?: number;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  parser: (value: any) => string;
}

export const Slider: React.FC<SliderProps> = ({
  min,
  max,
  valueType = SufixType.Euro,
  defaultValue = 0,
  notches = 20,
  onChange,
  disabled,
  required,
  parser,
  ...props
}) => {
  const [currentInputValue, setCurrentInputValue] = useState<string>(
    numberToLocaleString(defaultValue.toString()),
  );
  const [currentSliderValue, setCurrentSliderValue] = useState<number>(
    +defaultValue,
  );

  useEffect(() => {
    const newVal = +(defaultValue || 0);
    const currentVal = +parseFromCurrencyString(currentInputValue);
    if (Math.abs(newVal - currentVal) > 0.01) {
      setCurrentInputValue(numberToLocaleString(newVal.toString()));
      setCurrentSliderValue(newVal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);

  const validateAndConvertToString = useCallback(
    (value: number) => {
      let validatedValue = value;
      if (value <= min) {
        validatedValue = min;
      }
      if (value >= max) {
        validatedValue = max;
      }
      return validatedValue.toFixed(2);
    },
    [max, min],
  );

  const handleInputChange = useCallback(
    (value: string) => {
      let newValue = value;
      if (value && parser) {
        newValue = parser(value);
      }

      setCurrentInputValue(newValue);
      onChange(newValue);
    },
    [onChange, parser],
  );

  const handleBlur = (value: any) => {
    let decimalValue = min;
    if (value) {
      decimalValue = +parseFromCurrencyString(value);
    }
    const validatedString = validateAndConvertToString(decimalValue);

    handleInputChange(validatedString);
  };

  const handleSliderChange = (value: string) => {
    if (value) {
      handleInputChange((+value).toFixed(2));
    }
  };

  useEffect(() => {
    const decimalValue = +parseFromCurrencyString(currentInputValue);
    const validatedString = validateAndConvertToString(decimalValue);

    setCurrentSliderValue(+validatedString);
  }, [currentInputValue, max, min, validateAndConvertToString]);

  return (
    <Flex
      flexDirection="column"
      justifyContent="space-between"
      alignItems="center"
      width="100%"
      {...props}>
      <SliderNumberInput
        placeholder={`${min}`}
        min={min}
        max={max}
        value={parser(currentInputValue?.toString() || '')}
        onChange={(event) => handleInputChange(event.target.value)}
        onBlur={(event) => handleBlur(event.target.value)}
        onClear={() => handleInputChange(min.toFixed(2))}
        sufix={valueType}
      />
      <SliderBody min={min} max={max} valueType={valueType} notches={notches} disabled={disabled} required={required} currentSliderValue={currentSliderValue} handleSliderChange={handleSliderChange}/>
    </Flex>
  );
};
